// Чистая логика ручного подтверждения слияния: допуск баннера, причина
// недоступности кнопки, сборка тела запроса. Порядок проверок в
// `approvalBlocker` повторяет порядок на сервере, чтобы причина на экране
// совпадала с отказом, который вернул бы сервер.

import {
  type ApproveMergeRequest,
  type GateCheckId,
  type MergeGatePredicate,
  type MergeGateResponse,
  type PredicateClass,
} from "./api";
import { ApiError } from "../../api/apiError";
import { lastEscalationEntry } from "../queue/escalationCause";
import type { TaskResponse, TaskStatusEventResponse } from "../queue/api";

const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;

// Токен состояния, который человек видел в момент отметки, по id предиката.
export type AcknowledgedTokens = Readonly<Partial<Record<GateCheckId, string>>>;

export type ApprovalBlocker =
  | "pr_closed"
  | "approval_stale"
  | "ci_unknown"
  | "prohibiting_failed"
  | "not_acknowledged";

// `escalated` считается ожидающей подтверждения, если последний вход в
// `escalated` в журнале был из `awaiting_approval` (правило бэкенда).
export function isAwaitingMergeApproval(
  task: TaskResponse,
  events: readonly TaskStatusEventResponse[],
): boolean {
  if (task.status === "awaiting_approval") {
    return true;
  }
  if (task.status !== "escalated") {
    return false;
  }
  return lastEscalationEntry(events)?.from_status === "awaiting_approval";
}

// Ошибка чтения гейта означает «баннер не для этого человека/задачи»:
// нет права или задача не ждёт подтверждения (пара статус + код).
export function isGateHidden(error: Error): boolean {
  return (
    error instanceof ApiError &&
    ((error.status === HTTP_FORBIDDEN && error.code === "permission_denied") ||
      (error.status === HTTP_CONFLICT &&
        error.code === "not_awaiting_approval"))
  );
}

// Статус задачи сменился (не ждёт подтверждения / отменена) — карточку надо
// перечитать.
export function isTaskStateRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === HTTP_CONFLICT &&
    (error.code === "not_awaiting_approval" || error.code === "task_cancelled")
  );
}

export function isAcknowledged(
  predicate: MergeGatePredicate,
  acknowledged: AcknowledgedTokens,
): boolean {
  return (
    predicate.state_token !== null &&
    acknowledged[predicate.id] === predicate.state_token
  );
}

export function prohibitingFailures(
  gate: MergeGateResponse,
): readonly MergeGatePredicate[] {
  return gate.predicates.filter(
    (p) => p.outcome === "failed" && p.predicate_class === "prohibiting",
  );
}

function failedAcceptable(
  gate: MergeGateResponse,
): readonly MergeGatePredicate[] {
  return gate.predicates.filter(
    (p) => p.outcome === "failed" && p.predicate_class === "acceptable",
  );
}

export function approvalBlocker(
  gate: MergeGateResponse,
  acknowledged: AcknowledgedTokens,
): ApprovalBlocker | null {
  if (gate.pr_state === "merged") {
    return null;
  }
  if (gate.pr_state === "closed") {
    return "pr_closed";
  }
  if (gate.reviewed_head_sha !== gate.head_sha) {
    return "approval_stale";
  }
  if (gate.predicates.some((p) => p.outcome === "unknown")) {
    return "ci_unknown";
  }
  if (prohibitingFailures(gate).length > 0) {
    return "prohibiting_failed";
  }
  if (failedAcceptable(gate).some((p) => !isAcknowledged(p, acknowledged))) {
    return "not_acknowledged";
  }
  return null;
}

export function buildApproveRequest(
  gate: MergeGateResponse,
  acknowledged: AcknowledgedTokens,
): ApproveMergeRequest {
  return {
    head_sha: gate.head_sha,
    base_branch: gate.base_branch,
    acknowledged: failedAcceptable(gate).flatMap((p) =>
      p.state_token !== null && isAcknowledged(p, acknowledged)
        ? [{ predicate: p.id, state_token: p.state_token }]
        : [],
    ),
  };
}

export function predicatesByClass(
  gate: MergeGateResponse,
): Readonly<Record<PredicateClass, readonly MergeGatePredicate[]>> {
  return {
    prohibiting: gate.predicates.filter(
      (p) => p.predicate_class === "prohibiting",
    ),
    acceptable: gate.predicates.filter(
      (p) => p.predicate_class === "acceptable",
    ),
    not_participating: gate.predicates.filter(
      (p) => p.predicate_class === "not_participating",
    ),
  };
}
